import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TrendChart, type TrendSeries } from "../components/TrendChart";
import { getPatient, listSessions, getAnalysis } from "../../store/db";
import { buildTrendReportHtml, shareOrPrintReport, type TrendPoint } from "../../report/report";
import type { GaitView, Milestone, Patient } from "../../store/types";
import type { Scores } from "../../gait/scoring";

interface Measure {
  id: string;
  t: number;
  view: GaitView;
  milestone: Milestone;
  overall: number;
  cadence: number | null;
  confidence: string;
  scores?: Scores;
}

export function TrendScreen() {
  const { patientId } = useParams<{ patientId: string }>();
  const nav = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [measures, setMeasures] = useState<Measure[] | null>(null);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      const [p, sessions] = await Promise.all([getPatient(patientId), listSessions(patientId)]);
      const out: Measure[] = [];
      for (const s of sessions) {
        const a = await getAnalysis(s.id);
        if (a?.scores) {
          out.push({
            id: s.id,
            t: s.recordedAt,
            view: s.view,
            milestone: s.milestone,
            overall: a.scores.overall,
            cadence: a.clinical?.cadenceStepsPerMin ?? null,
            confidence: a.scores.confidence,
            scores: a.scores,
          });
        }
      }
      out.sort((a, b) => a.t - b.t);
      if (!cancelled) {
        setPatient(p ?? null);
        setMeasures(out);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (!measures) return <div className="card">Lädt…</div>;

  function exportTrendReport() {
    if (!measures || measures.length === 0) return;
    const pts: TrendPoint[] = measures.map((m) => ({
      t: m.t,
      view: m.view,
      milestone: m.milestone,
      overall: m.overall,
      cadence: m.cadence,
      confidence: m.confidence,
      scores: m.scores,
    }));
    void shareOrPrintReport(buildTrendReportHtml(patient, pts), `${patient?.code || "gait"}_trend`.replace(/\s+/g, "_"));
  }

  const byView = (view: GaitView): TrendSeries => ({
    view,
    pts: measures.filter((m) => m.view === view).map((m) => ({ t: m.t, v: m.overall, id: m.id })),
  });
  const scoreSeries = (["lateral", "frontal"] as GaitView[])
    .map(byView)
    .filter((s) => s.pts.length > 0);

  const cadencePts = measures.filter((m) => m.cadence !== null);
  const cadenceSeries: TrendSeries[] =
    cadencePts.length > 0
      ? (["lateral", "frontal"] as GaitView[])
          .map((view) => ({
            view,
            pts: cadencePts.filter((m) => m.view === view).map((m) => ({ t: m.t, v: m.cadence as number, id: m.id })),
          }))
          .filter((s) => s.pts.length > 0)
      : [];
  const cadVals = cadencePts.map((m) => m.cadence as number);
  const cadMin = cadVals.length ? Math.max(0, Math.floor(Math.min(...cadVals) - 10)) : 80;
  const cadMax = cadVals.length ? Math.ceil(Math.max(...cadVals) + 10) : 130;

  const latest = measures[measures.length - 1];
  const first = measures[0];
  const overallDelta = latest && first && latest.id !== first.id ? latest.overall - first.overall : null;

  return (
    <>
      <div className="review-actions">
        <div className="review-actions-meta">
          <strong>{patient?.code || "Verlauf"}</strong>
          <span className="muted">
            {measures.length} {measures.length === 1 ? "Messung" : "Messungen"}
          </span>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {measures.length > 0 && (
            <button className="btn" style={{ width: "auto", padding: "11px 16px" }} onClick={exportTrendReport}>
              Verlaufs-Report
            </button>
          )}
          <button className="btn secondary" style={{ width: "auto", padding: "11px 16px" }} onClick={() => nav("/")}>
            Neue Messung
          </button>
        </div>
      </div>

      {measures.length === 0 && (
        <div className="card">
          <strong>Noch kein Verlauf</strong>
          <p className="muted" style={{ marginTop: 8 }}>
            Für diese Person gibt es noch keine ausgewerteten Messungen.
          </p>
        </div>
      )}

      {measures.length > 0 && (
        <div className="card">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
            <strong>Gait-Score über Zeit</strong>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em" }}>{latest.overall}</div>
              {overallDelta !== null && (
                <span className={`pill trend ${overallDelta > 0 ? "up" : overallDelta < 0 ? "down" : "flat"}`}>
                  {overallDelta > 0 ? "▲ +" : overallDelta < 0 ? "▼ " : "± "}
                  {overallDelta} seit Beginn
                </span>
              )}
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <TrendChart series={scoreSeries} min={0} max={100} onPick={(id) => nav(`/review/${id}`)} />
          </div>
          <div className="legend" style={{ marginTop: 6 }}>
            <span><i style={{ background: "#7cc4ff" }} /> seitlich</span>
            <span><i style={{ background: "#5eead4" }} /> frontal</span>
            <span className="muted">Punkt antippen → Messung öffnen</span>
          </div>
        </div>
      )}

      {cadenceSeries.length > 0 && (
        <div className="card">
          <strong>Kadenz über Zeit</strong>
          <div style={{ marginTop: 12 }}>
            <TrendChart
              series={cadenceSeries}
              min={cadMin}
              max={cadMax}
              fmt={(v) => `${Math.round(v)}`}
              onPick={(id) => nav(`/review/${id}`)}
            />
          </div>
          <p className="muted" style={{ marginTop: 6 }}>Schritte pro Minute.</p>
        </div>
      )}

      {measures.length > 0 && (
        <div className="card">
          <strong>Messungen</strong>
          <div style={{ marginTop: 8 }}>
            {[...measures].reverse().map((m, i, arr) => {
              const prevSameView = arr.slice(i + 1).find((x) => x.view === m.view);
              const d = prevSameView ? m.overall - prevSameView.overall : null;
              return (
                <div className="finding" key={m.id}>
                  <div className="lbl">
                    {new Date(m.t).toLocaleDateString()} · {m.view === "frontal" ? "frontal" : "seitlich"}
                    <small>confidence {m.confidence}</small>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    {d !== null && (
                      <span className={`pill trend ${d > 0 ? "up" : d < 0 ? "down" : "flat"}`}>
                        {d > 0 ? "+" : ""}
                        {d}
                      </span>
                    )}
                    <span style={{ fontWeight: 700, minWidth: 26, textAlign: "right" }}>{m.overall}</span>
                    <a href={`#/review/${m.id}`}>öffnen</a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="disclaimer">
        Verlauf nur über kompatible Algorithmus-Versionen vergleichbar. Alle Daten bleiben auf diesem Gerät.
      </p>
    </>
  );
}
